import { useDispatch, useSelector } from 'react-redux';
import SearchWidgetSmall from '../components/SearchWidgetSmall';
import { getSearchPathWithRequest } from '../../../features/request/helpers';
import { getDurationOptions, getOccupancyOptions, OptionsValue } from '../static/staticOptions';
import { setArrival, setDuration, setOccupancy } from '../../../features/request/actions';
import { RootState } from '../../../config/redux/types';
import { RequestState } from '../../../features/request/types';

export interface SearchParameterOverrides {
  country_code?: string;
  region_1?: string;
  region_2?: string;
  accommodation_type?: string;
  duration_of_stay?: number;
}

interface SearchWidgetSmallContainerProps {
  withButton: boolean;
  searchParameterOverrides?: SearchParameterOverrides;
}

const defaultProps: Pick<SearchWidgetSmallContainerProps, 'searchParameterOverrides'> = {
  searchParameterOverrides: undefined,
};

function buildActionUrl(
  request: RequestState,
  searchParameterOverrides?: SearchParameterOverrides,
): string {
  if (!searchParameterOverrides) {
    return getSearchPathWithRequest(request);
  }
  const overridenRequest = { ...request };
  if (searchParameterOverrides.accommodation_type !== undefined) {
    overridenRequest.filterSet.accommodation_type = [searchParameterOverrides.accommodation_type];
  }
  if (searchParameterOverrides.country_code !== undefined) {
    overridenRequest.countryCode = searchParameterOverrides.country_code;
  }
  if (searchParameterOverrides.region_1 !== undefined) {
    overridenRequest.region = searchParameterOverrides.region_1;
  }
  if (searchParameterOverrides.region_2 !== undefined) {
    overridenRequest.city = searchParameterOverrides.region_2;
  }
  if (searchParameterOverrides.duration_of_stay) {
    overridenRequest.durationOfStay = searchParameterOverrides.duration_of_stay;
  }
  return getSearchPathWithRequest(overridenRequest);
}

export default function SearchWidgetSmallContainer({
  withButton,
  searchParameterOverrides,
}: SearchWidgetSmallContainerProps): JSX.Element {
  const request = useSelector((state: RootState) => state.request);
  const dispatch = useDispatch();

  function handleDurationChange(option: OptionsValue): void {
    const duration = typeof option.value === 'string' ? parseInt(option.value, 10) : option.value;
    dispatch(setDuration(!Number.isNaN(duration) ? duration : null));
  }

  function handleOccupancyChange(option: OptionsValue): void {
    const occupancy = typeof option.value === 'string' ? parseInt(option.value, 10) : option.value;
    dispatch(setOccupancy(!Number.isNaN(occupancy) ? occupancy : 1));
  }

  return (
    <SearchWidgetSmall
      options={{
        durations: getDurationOptions(),
        occupancies: getOccupancyOptions(),
      }}
      values={{
        arrival: request.arrival,
        duration: request.durationOfStay != null ? request.durationOfStay : null,
        occupancy: request.occupancy,
      }}
      actionUrl={buildActionUrl(request, searchParameterOverrides)}
      onArrivalChange={(date: moment.Moment | null): void => {
        dispatch(setArrival(date));
      }}
      onDurationChange={(duration: OptionsValue): void => handleDurationChange(duration)}
      onOccupancyChange={(option: OptionsValue): void => handleOccupancyChange(option)}
      withButton={withButton}
    />
  );
}

SearchWidgetSmallContainer.defaultProps = defaultProps;
